import { createTheme } from '@mui/material/styles'

// Shared design tokens that don't change between light and dark.
const typography = {
  fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
  fontSize: 13,
  h1: {
    fontSize: '1.75rem',
    fontWeight: 700,
    lineHeight: 1.3,
  },
  h2: {
    fontSize: '1.375rem',
    fontWeight: 600,
    lineHeight: 1.35,
  },
  h3: {
    fontSize: '1.125rem',
    fontWeight: 600,
    lineHeight: 1.4,
  },
  h4: {
    fontSize: '1rem',
    fontWeight: 600,
    lineHeight: 1.4,
  },
  h5: {
    fontSize: '0.875rem',
    fontWeight: 600,
    lineHeight: 1.45,
  },
  h6: {
    fontSize: '0.8125rem',
    fontWeight: 600,
    lineHeight: 1.5,
  },
  body1: {
    fontSize: '0.8125rem',
    lineHeight: 1.5,
  },
  body2: {
    fontSize: '0.75rem',
    lineHeight: 1.5,
  },
  caption: {
    fontSize: '0.6875rem',
    lineHeight: 1.5,
  },
  overline: {
    fontSize: '0.625rem',
    fontWeight: 600,
    letterSpacing: '0.08em',
    lineHeight: 1.5,
  },
  subtitle1: {
    fontSize: '0.8125rem',
    fontWeight: 500,
    lineHeight: 1.5,
  },
  subtitle2: {
    fontSize: '0.75rem',
    fontWeight: 500,
    lineHeight: 1.5,
  },
  button: {
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.8125rem',
  },
}

// Brand teal is constant across modes; light/dark tune surfaces and accents.
const palettes = {
  light: {
    mode: 'light',
    primary: {
      main: '#1B5E7A',
      light: '#47A7C7',
      dark: '#113C50',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#64748b',
      light: '#94a3b8',
      dark: '#475569',
      contrastText: '#ffffff',
    },
    background: {
      default: '#F5F7FA',
      paper: '#ffffff',
    },
    error: { main: '#ef4444' },
    warning: { main: '#f59e0b' },
    success: { main: '#10b981' },
    info: { main: '#3b82f6' },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
    divider: '#e2e8f0',
  },
  // Dark neon: deep slate/near-black surfaces with glowing teal + tuned
  // semantic neons that keep AA (>=4.5:1) contrast against the dark surfaces.
  dark: {
    mode: 'dark',
    primary: {
      main: '#47A7C7',
      light: '#7FD4EC',
      dark: '#1B5E7A',
      contrastText: '#04121A',
    },
    secondary: {
      main: '#94a3b8',
      light: '#cbd5e1',
      dark: '#64748b',
      contrastText: '#04121A',
    },
    background: {
      default: '#0A0E14',
      paper: '#111827',
    },
    error: { main: '#f87171' },
    warning: { main: '#fbbf24' },
    success: { main: '#34d399' },
    info: { main: '#60a5fa' },
    text: {
      primary: '#e5eef5',
      secondary: '#94a3b8',
    },
    divider: 'rgba(148, 163, 184, 0.18)',
  },
}

// Build a full MUI theme for the requested mode. Component overrides read from
// the resolved palette (via theme callbacks) so a single factory adapts both
// light and dark without per-page edits.
export function getTheme(mode = 'light') {
  const palette = palettes[mode] || palettes.light

  return createTheme({
    palette,
    typography,
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            padding: '6px 14px',
            fontWeight: 500,
            fontSize: '0.8125rem',
          },
          sizeSmall: {
            padding: '4px 10px',
            fontSize: '0.75rem',
          },
          sizeLarge: {
            padding: '8px 18px',
            fontSize: '0.875rem',
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 12,
            boxShadow:
              theme.palette.mode === 'dark'
                ? '0 1px 3px rgba(0,0,0,0.5)'
                : '0 1px 3px rgba(0,0,0,0.08)',
            border: `1px solid ${theme.palette.divider}`,
            backgroundImage: 'none',
          }),
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
              fontSize: '0.8125rem',
            },
            '& .MuiInputLabel-root': {
              fontSize: '0.8125rem',
            },
            '& .MuiFormHelperText-root': {
              fontSize: '0.6875rem',
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            fontSize: '0.8125rem',
          },
          input: {
            fontSize: '0.8125rem',
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: '0.8125rem',
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            fontSize: '0.8125rem',
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            fontSize: '0.6875rem',
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            fontSize: '0.8125rem',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: '0.8125rem',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            fontSize: '0.75rem',
            padding: '10px 12px',
          },
          head: ({ theme }) => ({
            fontWeight: 600,
            fontSize: '0.75rem',
            backgroundColor:
              theme.palette.mode === 'dark'
                ? 'rgba(148, 163, 184, 0.06)'
                : '#f8fafc',
          }),
        },
      },
      MuiTablePagination: {
        styleOverrides: {
          root: {
            fontSize: '0.75rem',
          },
          selectLabel: {
            fontSize: '0.75rem',
          },
          displayedRows: {
            fontSize: '0.75rem',
          },
          select: {
            fontSize: '0.75rem',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            fontWeight: 500,
            fontSize: '0.6875rem',
          },
          sizeSmall: {
            fontSize: '0.625rem',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            fontSize: '0.8125rem',
            fontWeight: 500,
            textTransform: 'none',
            minHeight: 42,
          },
        },
      },
      MuiTabs: {
        // Default every tab strip to scrollable so many/long tabs never force
        // horizontal page overflow on small screens. Pages can still override
        // with an explicit `variant` (e.g. "fullWidth"/"standard") when desired.
        defaultProps: {
          variant: 'scrollable',
          scrollButtons: 'auto',
          allowScrollButtonsMobile: true,
        },
        styleOverrides: {
          root: {
            minHeight: 42,
          },
        },
      },
      MuiBreadcrumbs: {
        styleOverrides: {
          root: {
            fontSize: '0.75rem',
          },
          li: {
            fontSize: '0.75rem',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          // Make every dialog full-screen on phones so wide content (forms,
          // tables, JSON blocks) can never overflow the viewport horizontally.
          // Mirrors the shared Modal component's `fullScreen={isMobile}` so raw
          // <Dialog> usages behave consistently without per-instance changes.
          paper: ({ theme }) => ({
            [theme.breakpoints.down('sm')]: {
              margin: 0,
              width: '100%',
              maxWidth: '100%',
              height: '100%',
              maxHeight: '100%',
              borderRadius: 0,
            },
          }),
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontSize: '1rem',
            fontWeight: 600,
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            fontSize: '0.8125rem',
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontSize: '0.6875rem',
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            fontSize: '0.8125rem',
          },
          message: {
            fontSize: '0.8125rem',
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          primary: {
            fontSize: '0.8125rem',
          },
          secondary: {
            fontSize: '0.75rem',
          },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          title: {
            fontSize: '0.9375rem',
            fontWeight: 600,
          },
          subheader: {
            fontSize: '0.75rem',
          },
        },
      },
    },
  })
}

// Back-compat default export: the light theme.
const theme = getTheme('light')

export default theme
